import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import draftImg from '../../resources/images/draft_vid.png';
import Header from '../../Components/Header/Header';
import { useAuthContext } from '../../hooks/UseAuthContext';
import Loader from '../../Components/Loader/Loader';
import api from '../../api';
// import './completedvids.css';

const VIDEOS_PER_PAGE = 12;

function Drafts() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const { user } = useAuthContext();

  // Fetch users for filtering
  useEffect(() => {
    api.get('/users', {
      headers: { Authorization: `Bearer ${user.token}` }
    })
      .then(response => setUsers(response.data))
      .catch(error => console.error('Error fetching users', error));
  }, [user?.token]);

  // Fetch video drafts
  useEffect(() => {
    api.get('/video-drafts')
      .then(response => {
        const draftOnly = response.data.filter(video => video.status === 'draft');
        setVideos(draftOnly);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching drafts:', error);
        setLoading(false);
      });
  }, []);

  const handleNavigate = (id, data) => {
    navigate(`/compose/${id}`, { state: { videoUrl: data } });
  };

  // Filter and sort videos
  const filteredVideos = useMemo(() => {
    const filtered = selectedUserEmail
      ? videos.filter(video => video.user_id === selectedUserEmail)
      : videos;

    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [videos, selectedUserEmail]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredVideos.length / VIDEOS_PER_PAGE);
  const startIndex = (currentPage - 1) * VIDEOS_PER_PAGE;
  const endIndex = startIndex + VIDEOS_PER_PAGE;
  const currentVideos = filteredVideos.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedUserEmail]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="completed-vids-container">
      <Header />

      <div className="completed-vids">
        {/* Header with Filter */}
        <div className="complete-vid-header">
          <h2>📝 Video Drafts</h2>
          <h3>Filter By User</h3>
          <select
            className="select-user-video"
            value={selectedUserEmail}
            onChange={(e) => setSelectedUserEmail(e.target.value)}
          >
            <option value="">— All Users —</option>
            {users?.map((user, index) => (
              <option key={index} value={user.email}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
        </div>

        {/* Video Grid */}
        {currentVideos.length > 0 ? (
          <>
            <div className="videos">
              {currentVideos.map((video) => (
                <div
                  key={video._id}
                  className="video-element"
                  onClick={() => handleNavigate(video._id, video)}
                >
                  <img src={draftImg} alt="Draft thumbnail" />
                  <h3>{video.title || 'Untitled Draft'}</h3>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {startIndex + 1}–{Math.min(endIndex, filteredVideos.length)} of {filteredVideos.length}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  ← Prev
                </button>

                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span key={`dots-${index}`} className="page-dots">
                      ⋯
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={currentPage === page ? 'active' : ''}
                      aria-label={`Page ${page}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>No drafts found.</p>
          </div>
        )}
      </div>

      {loading && <Loader />}
    </div>
  );
}

export default Drafts;